import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import TradeDrawer from "./TradeDrawer";
import TradeCards from "./TradeCards";
import { useSwipeClose } from "./useSwipeClose";
import { ERA_VIEWS as ERA_VIEWS_SHARED } from "./eras";
import { dteOf, tradingDaysOf, expiryOf } from "./expiry";
import { chargesBreakdown, sumCharges, CHARGE_KEYS } from "./charges";
import { dailyPeakCapital, peakConcurrentCapital } from "./capitalUsage";

/* ===== Analytics — trigger-level profitability from the live trade log =====
   Groups every closed trade by direction @ fib level + confluence class.
   Filter chips (index / direction / fib / confluence / strategy) are all
   active by default — deselect to exclude. Clicking a trigger row opens a
   right-side drawer with each trade (date, instrument, P&L…); clicking a
   trade there opens the full TradeDrawer. */

const rupees = (v) => `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;

function confClass(t) {
  const c = t.confluence || [];
  const v = c.some(x => String(x).includes("VWAP"));
  const s = c.some(x => String(x).includes("S/R"));
  if (v && s) return "VWAP+S/R";
  if (v) return "VWAP only";
  if (s) return "S/R only";
  return "none";
}
const fibOf = (t) => t.fibLevel || "no fib";
const triggerOf = (t) => `${t.type || "?"} @ ${fibOf(t)} + ${confClass(t)}`;
const instrumentOf = (t) => (t.strategy === "fib-vwap-guarded" ? `${t.index || "?"} Guarded` : (t.index || "?"));
const dayOf = (t) => {
  const d = new Date(t.time || t.placedAt);
  return isNaN(d) ? "?" : d.toLocaleDateString("en-CA");
};

/* group trades by keyFn -> [{label, trades, n, wins, pnl}] */
function groupStats(trades, keyFn, sortDayDesc = false) {
  const m = new Map();
  for (const t of trades) {
    const k = keyFn(t);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(t);
  }
  const rows = [...m.entries()].map(([label, ts]) => ({
    label, trades: ts, n: ts.length,
    wins: ts.filter(t => t.netPnl > 0).length,
    pnl: ts.reduce((a, t) => a + (t.netPnl || 0), 0),
  }));
  return sortDayDesc ? rows.sort((a, b) => b.label.localeCompare(a.label))
                     : rows.sort((a, b) => b.pnl - a.pnl);
}

const TIER_BUCKETS = ["No booking", "Tier 1 only", "Tier 2 only", "Both tiers", "Partial (legacy)"];
function tierOf(t) {
  const t1 = !!t.tier1Done, t2 = !!t.tier2Done;
  if (t1 && t2) return "Both tiers";
  if (t1) return "Tier 1 only";
  if (t2) return "Tier 2 only";
  if (t.trailExitDone || t.partialExitDone) return "Partial (legacy)";
  return "No booking";
}

const MODE_BUCKETS = ["🔴 LIVE", "SIM"];
const modeOf = (t) => (t.simulated === false ? "🔴 LIVE" : "SIM");
const EXPIRY_BUCKETS = ["Expiry day", "1 DTE", "2+ DTE", "unknown"];
function expiryBucket(t) {
  const d = dteOf(t);
  if (d == null) return "unknown";
  if (d <= 0) return "Expiry day";
  if (d === 1) return "1 DTE";
  return "2+ DTE";
}

const VIX_BUCKETS = ["<12", "12-13", "13-14", "14-16", "16-20", ">20", "unknown"];
function vixBucket(t) {
  const v = t.vixEntry;
  if (v == null || isNaN(v)) return "unknown";
  if (v < 12) return "<12";
  if (v < 13) return "12-13";
  if (v < 14) return "13-14";
  if (v < 16) return "14-16";
  if (v < 20) return "16-20";
  return ">20";
}

function Chip({ label, active, onClick, count }) {
  return (
    <button onClick={onClick} style={{
      background: active ? "#1d4ed8" : "var(--panel3)", color: active ? "#fff" : "var(--muted2)",
      border: `1px solid ${active ? "#3b82f6" : "var(--border2)"}`, borderRadius: 14,
      padding: "3px 10px", fontSize: 11, cursor: "pointer", opacity: active ? 1 : 0.7,
    }} title={active ? "Click to exclude" : "Click to include"}>
      {label}{count != null ? ` (${count})` : ""}
    </button>
  );
}

function ChipGroup({ title, values, selected, onToggle, onSetAll, counts }) {
  const allOn = values.every(v => selected.has(v));
  const linkStyle = { background: "none", border: "none", color: "#3b82f6", fontSize: 10, cursor: "pointer", padding: "0 2px", textDecoration: "underline" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", width: 78, flexShrink: 0 }}>{title}</span>
      <span style={{ display: "flex", gap: 2, flexShrink: 0 }}>
        <button style={{ ...linkStyle, color: allOn ? "var(--faint2)" : "#3b82f6" }} disabled={allOn} onClick={() => onSetAll(true)} title={`Select all ${title}`}>all</button>
        <span style={{ color: "var(--border2)", fontSize: 10 }}>/</span>
        <button style={{ ...linkStyle, color: selected.size === 0 ? "var(--faint2)" : "#3b82f6" }} disabled={selected.size === 0} onClick={() => onSetAll(false)} title={`Deselect all ${title}`}>none</button>
      </span>
      {values.map(v => (
        <Chip key={v} label={v} active={selected.has(v)} onClick={() => onToggle(v)} count={counts?.[v]} />
      ))}
    </div>
  );
}

/* Mobile stacked renderer for aggregate tables (shown <640px via .tl-cards) */
function MobileRows({ items }) {
  return (
    <div className="tl-cards">
      {items.map(it => (
        <div key={it.key} onClick={it.onClick}
          style={{ background: "var(--panel3)", borderRadius: 8, padding: "8px 10px", marginBottom: 6, cursor: it.onClick ? "pointer" : "default", outline: it.active ? "1px solid #3b82f6" : "none" }}>
          <div style={{ fontWeight: 700, fontSize: 12.5, color: "var(--text2)" }}>{it.label}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 11, color: "var(--dim2)", marginTop: 3 }}>
            {it.stats.map(st => <span key={st[0]}>{st[0]} <b className={st[2] || ""}>{st[1]}</b></span>)}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Paging (user, 01 Oct 2026): long tables cap at 10 rows with Prev/Next.
   `render` receives the current page's slice; nav renders only when needed. */
function Paged({ items, per = 10, render }) {
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [items.length]);
  const pages = Math.max(1, Math.ceil(items.length / per));
  const p = Math.min(page, pages - 1);
  return (
    <>
      {render(items.slice(p * per, (p + 1) * per))}
      {pages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, fontSize: 11, color: "var(--muted2)", margin: "6px 2px" }}>
          <button className="btn btn-sm btn-secondary" disabled={p === 0} onClick={() => setPage(p - 1)}>‹ Prev</button>
          <span>page {p + 1} / {pages} · {items.length} rows</span>
          <button className="btn btn-sm btn-secondary" disabled={p >= pages - 1} onClick={() => setPage(p + 1)}>Next ›</button>
        </div>
      )}
    </>
  );
}

/* AI usage + regime strip (user, 03 Oct 2026): tokens & money, plus today's
   advisory regime card. Reads /api/ai/status and /api/ai/regime. */
function AiUsageStrip() {
  const [use, setUse] = useState(null);
  const [card, setCard] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get("/api/ai/status", { timeout: 15000 }); setUse(r.data?.tokenUsage || null); } catch {}
      try { const r = await axios.get("/api/ai/regime", { timeout: 15000 }); setCard(r.data?.card || null); } catch {}
    })();
  }, []);
  if (!use && !card) return null;
  const ist = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const today = use?.byDay?.[ist];
  const inr = (usd) => `₹${(usd * (use?.usdInr || 89)).toFixed(2)}`;
  return (
    <div style={{ fontSize: 11, color: "var(--muted2)", background: "var(--panel3)", borderRadius: 6, padding: "6px 10px", marginBottom: 8, display: "flex", gap: 14, flexWrap: "wrap" }}>
      {use && (
        <span title="All LLM calls across reviewer, regime card and analyzer — logged with prompt/completion split in data/ai_token_usage.json">
          🤖 AI usage — today: <b>{today ? `${today.calls} calls · ${today.tokens.toLocaleString("en-IN")} tok · ${inr(today.costUsd)}` : "none"}</b>
          {" · "}all-time: <b>{(use.total || 0).toLocaleString("en-IN")} tok · {inr(use.costUsd || 0)}</b>
          <span style={{ color: "var(--faint2)" }}> (${(use.costUsd || 0).toFixed(3)} @ ₹{use.usdInr || 89}/$)</span>
        </span>
      )}
      {card && (
        <span title={card.note || ""}>
          📋 Regime card {card.date}: <b className={card.risk_regime === "RISK_OFF" ? "negative" : card.risk_regime === "RISK_ON" ? "positive" : ""}>{card.risk_regime}</b>
          {(card.event_flags || []).concat(card.crypto_flags || []).length > 0 && <> · {(card.event_flags || []).concat(card.crypto_flags || []).join(", ")}</>}
          <span style={{ color: "var(--faint2)" }}> (advisory — stamped on trades, gates nothing)</span>
        </span>
      )}
    </div>
  );
}

/* Fib depth paper filters (25 Oct 2026): trades now stamp the swing their
   ladder came from + measured entry depth. Two filters from the recorded-trade
   depth study are scored here as what-ifs — "with filter" = actual book minus
   the trades the filter flags. Forward = stamped trades (the real test);
   history = swings reconstructed from same-day trade pairs (the study's data). */
export function FibDepthPaperFilters() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get("/api/fib-depth/paper-filters", { timeout: 15000 })
      .then(r => setD(r.data)).catch(e => setErr(e.message));
  }, []);
  if (err) return <div style={{ fontSize: 11, color: "var(--muted2)" }}>Unavailable: {err}</div>;
  if (!d) return <div style={{ fontSize: 11, color: "var(--muted2)" }}>Loading…</div>;
  const NAMES = { deadZone: "Dead zone — entry depth 0.60–0.65", bounce: "Bounce — trade bets on a reversal at the level", both: "Both filters" };
  const table = (s, title) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{title} <span style={{ fontWeight: 400, color: "var(--muted2)" }}>({s.withDepth} of {s.trades} closed trades have a depth)</span></div>
      <div className="tl-table-wrap">
        <table className="stocks-table" style={{ width: "100%" }}>
          <thead><tr><th>Filter</th><th>Would skip</th><th>Their net</th><th>Book actual</th><th>Book with filter</th><th>Δ</th></tr></thead>
          <tbody>
            {["deadZone", "bounce", "both"].map(k => {
              const f = s[k];
              return (
                <tr key={k}>
                  <td style={{ fontWeight: 600 }}>{NAMES[k]}</td>
                  <td>{f.skipped} <span style={{ color: "var(--muted2)", fontSize: 10 }}>({f.skippedWins} winners)</span></td>
                  <td className={f.skippedNet >= 0 ? "positive" : "negative"}>{rupees(f.skippedNet)}</td>
                  <td className={f.bookNet >= 0 ? "positive" : "negative"}>{rupees(f.bookNet)}</td>
                  <td className={f.bookNetWithFilter >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(f.bookNetWithFilter)}</td>
                  <td className={f.delta >= 0 ? "positive" : "negative"}>{rupees(f.delta)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
  return (
    <>
      {d.stampedOnly.withDepth > 0
        ? table(d.stampedOnly, "Forward test — trades stamped live since 25 Oct 2026")
        : <div style={{ fontSize: 11, color: "var(--muted2)", marginBottom: 10 }}>Forward test — no stamped trades closed yet (stamping started 25 Oct 2026).</div>}
      {table(d.all, "History — swings reconstructed from same-day trade pairs")}
      <div style={{ fontSize: 10, color: "var(--faint2)", lineHeight: 1.5 }}>
        History is the sample these filters were found on, so it flatters them; only the forward rows are a fair test.
        A skipped trade could have freed the slot for another signal — not modelled. Depth 0 = the swing end the
        pullback started from, 1 = the other end. Neither filter changes live trading.
      </div>
    </>
  );
}

/* Collapsible results panel; collapsed state persists per panel */
function Panel({ id, title, subtitle, children }) {
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem("anx-panel-" + id) === "1"; } catch { return false; } });
  const toggle = () => setCollapsed(c => { const n = !c; try { localStorage.setItem("anx-panel-" + id, n ? "1" : "0"); } catch {} return n; });
  return (
    <div style={{ background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: collapsed ? 0 : 6 }}>
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", fontWeight: 700 }}>
          {title} {subtitle && <span style={{ fontWeight: 400, textTransform: "none" }}>{subtitle}</span>}
        </div>
        <button onClick={toggle} title={collapsed ? "Expand" : "Collapse"}
          style={{ background: "none", border: "1px solid var(--border2)", borderRadius: 4, color: "var(--muted2)", fontSize: 11, padding: "0 7px", cursor: "pointer", lineHeight: "16px" }}>
          {collapsed ? "▸" : "▾"}
        </button>
      </div>
      {!collapsed && children}
    </div>
  );
}

/* Right-side drawer listing all trades of one slice (trigger / instrument / day).
   `breakdowns` = [{title, rows: groupStats(...)}] rendered above the trade list;
   clicking a breakdown row narrows the trade list to it (click again to clear). */
function TriggerDrawer({ group, trades, breakdowns, onClose, onPickTrade, tradeOpen }) {
  const [fullscreen, setFullscreen] = useState(false);
  const swipe = useSwipeClose(onClose);
  const [sub, setSub] = useState(null); // {title, label, trades}
  useEffect(() => { setSub(null); }, [group]);
  useEffect(() => {
    const onKey = (e) => {
      if (tradeOpen) return; // the TradeDrawer on top handles keys
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "f" || e.key === "F") { e.preventDefault(); setFullscreen(f => !f); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, tradeOpen]);

  const wins = trades.filter(t => t.netPnl > 0).length;
  const pnl = trades.reduce((a, t) => a + (t.netPnl || 0), 0);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1190 }} />
      <div {...swipe.handlers} style={{
        position: "fixed", top: 0, right: 0, height: "100vh", zIndex: 1191,
        width: fullscreen ? "100vw" : "min(720px, 96vw)",
        background: "var(--panel2)", borderLeft: "1px solid var(--border2)", boxShadow: "-8px 0 30px rgba(0,0,0,0.5)",
        display: "flex", flexDirection: "column",
        transform: swipe.dx ? `translateX(${swipe.dx}px)` : "none",
        transition: swipe.dx ? "none" : "width 0.15s ease, transform 0.15s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--panel3)" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text2)" }}>{group}</div>
            <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 2 }}>
              {trades.length} trades · {wins}W {trades.length - wins}L ({trades.length ? Math.round(wins / trades.length * 100) : 0}%) ·{" "}
              <span style={{ color: pnl >= 0 ? "#34d399" : "#f87171", fontWeight: 700 }}>{rupees(pnl)}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 10, color: "var(--faint2)" }}>F full screen · Esc close</span>
            <button onClick={() => setFullscreen(f => !f)} style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
              {fullscreen ? "⇥ Exit Full" : "⇤ Full"}
            </button>
            <button onClick={onClose} style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>✕</button>
          </div>
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "8px 16px" }}>
          {(breakdowns || []).map(bd => (
            <div key={bd.title} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", fontWeight: 700, margin: "4px 0" }}>{bd.title}</div>
              <Paged items={bd.rows} render={pageBd => (<>
              <MobileRows items={pageBd.map(r => {
                const active = sub && sub.title === bd.title && sub.label === r.label;
                return { key: r.label, label: r.label, active,
                  onClick: () => setSub(active ? null : { title: bd.title, label: r.label, trades: r.trades }),
                  stats: [["N", r.n], ["WR", Math.round(r.wins / r.n * 100) + "%"], ["Net", rupees(r.pnl), r.pnl >= 0 ? "positive" : "negative"]] };
              })} />
              <div className="tl-table-wrap">
              <table className="stocks-table" style={{ width: "100%" }}>
                <thead><tr><th></th><th>Trades</th><th>WR</th><th>Net P&L</th></tr></thead>
                <tbody>
                  {pageBd.map(r => {
                    const active = sub && sub.title === bd.title && sub.label === r.label;
                    return (
                      <tr key={r.label} onClick={() => setSub(active ? null : { title: bd.title, label: r.label, trades: r.trades })}
                        style={{ cursor: "pointer", outline: active ? "1px solid #3b82f6" : "none" }}
                        title={active ? "Click to show all trades again" : "Click to narrow the trade list below"}>
                        <td style={{ fontWeight: 600 }}>{r.label}</td>
                        <td>{r.n}</td>
                        <td>{Math.round(r.wins / r.n * 100)}%</td>
                        <td className={r.pnl >= 0 ? "positive" : "negative"}>{rupees(r.pnl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              </>)} />
            </div>
          ))}
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", fontWeight: 700, margin: "4px 0" }}>
            Trades ({(sub ? sub.trades : trades).length}{sub ? ` — ${sub.label}` : ""})
            {sub && <button onClick={() => setSub(null)} style={{ marginLeft: 8, background: "none", border: "none", color: "#3b82f6", fontSize: 10, cursor: "pointer", textDecoration: "underline" }}>show all</button>}
          </div>
          <Paged items={sub ? sub.trades : trades} render={pageTrades => (<>
          <TradeCards trades={pageTrades} onSelect={onPickTrade} />
          <div className="tl-table-wrap">
          <table className="stocks-table" style={{ width: "100%" }}>
            <thead><tr>
              <th>Date</th><th>Exit time</th><th>Index</th><th>Instrument</th><th>Strategy</th><th>Qty</th>
              <th>Entry</th><th>Buy Prem</th><th>Sell Prem</th><th>Exit</th><th>Net P&L</th>
            </tr></thead>
            <tbody>
              {pageTrades.map((t, i) => (
                <tr key={t.id || i} style={{ cursor: "pointer" }} title="Click for full trade details"
                  onClick={() => onPickTrade(t)}>
                  <td style={{ whiteSpace: "nowrap" }}>{new Date(t.time || t.placedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{t.exitTime ? new Date(t.exitTime).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                  <td>{t.index || "-"}</td>
                  <td style={{ fontSize: 11 }}>{t.tradingsymbol || `${t.signalType || ""} ${t.strike || ""}`}</td>
                  <td style={{ fontSize: 10, color: "var(--dim2)" }}>{t.strategy || "-"}</td>
                  <td>{t.qty ?? "-"}</td>
                  <td>{t.entry != null ? t.entry.toFixed(1) : "-"}</td>
                  <td>{t.entryPremium != null ? `₹${t.entryPremium.toFixed(1)}` : "-"}</td>
                  <td>{t.exitPremium != null ? `₹${t.exitPremium.toFixed(1)}` : "-"}</td>
                  <td style={{ fontSize: 11 }}>{t.exitReason || t.status}</td>
                  <td className={t.netPnl >= 0 ? "positive" : "negative"}>{t.netPnl != null ? rupees(t.netPnl) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>)} />
        </div>
      </div>
    </>
  );
}

export default function Analytics() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sortKey, setSortKey] = useState("pnl");
  const [minN, setMinN] = useState(5);
  // Opens in the "Era 2 · New Era" view by default (open-ended CUSTOM window)
  const [period, setPeriod] = useState("CUSTOM"); // TODAY | 7D | 30D | 90D | ALL | CUSTOM
  const [fromDate, setFromDate] = useState(ERA_VIEWS_SHARED[0].from);
  const [toDate, setToDate] = useState("");
  const [drawerGroup, setDrawerGroup] = useState(null); // { label, trades }
  const [savedViews, setSavedViews] = useState(() => {
    try { return JSON.parse(localStorage.getItem("analyticsViews") || "[]"); } catch { return []; }
  });
  const persistViews = (views) => { setSavedViews(views); try { localStorage.setItem("analyticsViews", JSON.stringify(views)); } catch {} };
  const saveView = () => {
    const name = window.prompt("Name this view:", "");
    if (!name || !sel) return;
    const view = {
      name, period, fromDate, toDate, sortKey, minN,
      sel: Object.fromEntries(Object.entries(sel).map(([k, v]) => [k, [...v]])),
    };
    persistViews([...savedViews.filter(v => v.name !== name), view]);
  };
  const applyView = (v) => {
    setPeriod(v.period || "ALL"); setFromDate(v.fromDate || ""); setToDate(v.toDate || "");
    setSortKey(v.sortKey || "pnl"); setMinN(v.minN || 5);
    setSel(Object.fromEntries(Object.entries(v.sel || {}).map(([k, arr]) => [k, new Set(arr)])));
  };
  const deleteView = (name) => {
    if (!window.confirm(`Delete saved view "${name}"?`)) return;
    persistViews(savedViews.filter(v => v.name !== name));
  };
  const ERA_VIEWS = ERA_VIEWS_SHARED;
  const applyEra = (e) => {
    setPeriod("CUSTOM"); setFromDate(e.from); setToDate("");
    setSortKey("pnl"); setMinN(5);
    setSel({
      index: new Set(facets.index), direction: new Set(facets.direction), fib: new Set(facets.fib),
      confluence: new Set(facets.confluence), strategy: new Set(facets.strategy), vix: new Set(facets.vix), tier: new Set(facets.tier), expiryb: new Set(facets.expiryb),
    });
  };
  const [drawerTrade, setDrawerTrade] = useState(null);
  const [chargesView, setChargesView] = useState("day"); // day | instrument | trade

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const res = await axios.get("/api/monitor/status", { timeout: 30000 });
      setTrades((res.data?.trades || []).filter(t => t.status === "CLOSED" && t.netPnl != null));
    } catch (e) { setErr(e.message || "failed to load trades"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Date/period filter — applied before everything else
  const dateFiltered = useMemo(() => {
    const dayStr = (t) => {
      const d = new Date(t.time || t.placedAt);
      return isNaN(d) ? "" : d.toLocaleDateString("en-CA");
    };
    if (period === "CUSTOM") {
      return trades.filter(t => {
        const d = dayStr(t);
        return d && (!fromDate || d >= fromDate) && (!toDate || d <= toDate);
      });
    }
    if (period === "ALL") return trades;
    const days = period === "TODAY" ? 0 : parseInt(period, 10); // "7D" -> 7
    const cutoff = new Date(Date.now() - days * 86400000).toLocaleDateString("en-CA");
    return trades.filter(t => dayStr(t) >= cutoff);
  }, [trades, period, fromDate, toDate]);

  // Facet values discovered from data
  const facets = useMemo(() => {
    const uniq = (fn, order) => {
      const vals = [...new Set(trades.map(fn))];
      return order ? order.filter(v => vals.includes(v)).concat(vals.filter(v => !order.includes(v))) : vals.sort();
    };
    return {
      index: uniq(instrumentOf),
      direction: uniq(t => t.type || "?", ["LONG", "SHORT"]),
      fib: uniq(fibOf, ["23.6%", "38.2%", "50%", "61.8%", "78.6%", "no fib"]),
      confluence: uniq(confClass, ["VWAP only", "S/R only", "VWAP+S/R", "none"]),
      strategy: uniq(t => t.strategy || "?"),
      vix: uniq(vixBucket, VIX_BUCKETS),
      tier: uniq(tierOf, TIER_BUCKETS),
      expiryb: uniq(expiryBucket, EXPIRY_BUCKETS),
      mode: uniq(modeOf, MODE_BUCKETS),
    };
  }, [trades]);

  // All facet values selected by default; deselect to exclude
  const [sel, setSel] = useState(null);
  useEffect(() => {
    if (trades.length) {
      setSel(prev => prev || {
        index: new Set(facets.index), direction: new Set(facets.direction), fib: new Set(facets.fib),
        confluence: new Set(facets.confluence), strategy: new Set(facets.strategy), vix: new Set(facets.vix), tier: new Set(facets.tier), expiryb: new Set(facets.expiryb),
        mode: new Set(facets.mode),
      });
    }
  }, [trades, facets]);

  const toggle = (facet, v) => setSel(prev => {
    const next = { ...prev, [facet]: new Set(prev[facet]) };
    if (next[facet].has(v)) next[facet].delete(v); else next[facet].add(v);
    return next;
  });

  const setAll = (facet, on) => setSel(prev => ({ ...prev, [facet]: new Set(on ? facets[facet] : []) }));

  const filtered = useMemo(() => {
    if (!sel) return [];
    return dateFiltered.filter(t =>
      sel.index.has(instrumentOf(t)) && sel.direction.has(t.type || "?") && sel.fib.has(fibOf(t)) &&
      sel.confluence.has(confClass(t)) && sel.strategy.has(t.strategy || "?") &&
      (!sel.vix || sel.vix.has(vixBucket(t))) &&
      (!sel.tier || sel.tier.has(tierOf(t))) &&
      (!sel.expiryb || sel.expiryb.has(expiryBucket(t))) &&
      (!sel.mode || sel.mode.has(modeOf(t))));
  }, [dateFiltered, sel]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const label = `${t.type || "?"} @ ${fibOf(t)} + ${confClass(t)}`;
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(t);
    }
    const rows = [...map.entries()].map(([label, ts]) => {
      const wins = ts.filter(t => t.netPnl > 0).length;
      const pnl = ts.reduce((a, t) => a + (t.netPnl || 0), 0);
      return { label, ts, n: ts.length, wins, wr: ts.length ? wins / ts.length : 0, pnl, avg: ts.length ? pnl / ts.length : 0 };
    }).filter(r => r.n >= minN);
    const cmp = { pnl: (a, b) => b.pnl - a.pnl, wr: (a, b) => b.wr - a.wr, n: (a, b) => b.n - a.n, avg: (a, b) => b.avg - a.avg };
    return rows.sort(cmp[sortKey] || cmp.pnl);
  }, [filtered, sortKey, minN]);

  const byInstrument = useMemo(() => groupStats(filtered, instrumentOf), [filtered]);
  const byVix = useMemo(() =>
    groupStats(filtered, vixBucket).sort((a, b) => VIX_BUCKETS.indexOf(a.label) - VIX_BUCKETS.indexOf(b.label)),
  [filtered]);
  const byDay = useMemo(() =>
    groupStats(filtered, dayOf, true).map(r => ({ ...r, peak: peakConcurrentCapital(r.trades).peak })),
  [filtered]);

  const tierFunnel = useMemo(() => {
    const bookCat = (t) => {
      if (t.tier1Done && t.tier2Done) return "Both tiers";
      if (t.tier1Done || t.tier2Done) return "1 tier";
      if (t.trailExitDone || t.partialExitDone) return "Partial (legacy)";
      return "No booking";
    };
    const exitCat = (t) => t.exitReason || t.status || "?";
    const m = new Map();
    for (const t of filtered) {
      const k = `${bookCat(t)} → ${exitCat(t)}`;
      if (!m.has(k)) m.set(k, { cat: bookCat(t), trades: [] });
      m.get(k).trades.push(t);
    }
    const ORDER = { "Both tiers": 0, "1 tier": 1, "Partial (legacy)": 2, "No booking": 3 };
    const rows = [...m.entries()].map(([label, { cat, trades: ts }]) => ({
      label, cat, trades: ts, n: ts.length,
      wins: ts.filter(t => t.netPnl > 0).length,
      pnl: ts.reduce((a, t) => a + (t.netPnl || 0), 0),
      bookedLeg: ts.reduce((a, t) => a + (t.trailExitPnl || 0), 0),
    })).sort((a, b) => (ORDER[a.cat] - ORDER[b.cat]) || (b.pnl - a.pnl));
    return {
      rows,
      t1: filtered.filter(t => t.tier1Done).length,
      t2: filtered.filter(t => t.tier2Done).length,
      both: filtered.filter(t => t.tier1Done && t.tier2Done).length,
    };
  }, [filtered]);

  // AI live-trade reviewer scoreboard (advisory suggestions recorded on trades)
  const aiStats = useMemo(() => {
    const reviewed = filtered.filter(t => Array.isArray(t.aiReviews) && t.aiReviews.length);
    const reviews = reviewed.flatMap(t => t.aiReviews.map(r => ({ ...r, trade: t })));
    const byDetector = {};
    for (const r of reviews) {
      const d = byDetector[r.detector] = byDetector[r.detector] || { n: 0, exits: 0 };
      d.n += 1; if (r.verdict === "EXIT") d.exits += 1;
    }
    const withDelta = reviewed.filter(t => t.aiExitDelta != null);
    // DEDUPED counterfactual (08 Oct 2026): one exit per trade — the FIRST
    // EXIT-verdict review; delta = (premium at that review − actual exit
    // premium) × qty at review. Summing every review double-counts trades
    // flagged twice (you cannot exit the same trade at two prices).
    let dedupSum = 0, dedupN = 0;
    const dedupByDet = {};
    for (const t of reviewed) {
      if (t.status !== "CLOSED" || t.exitPremium == null) continue;
      const first = (t.aiReviews || []).filter(r => r.verdict === "EXIT" && r.premium != null)
        .sort((a, b) => String(a.time).localeCompare(String(b.time)))[0];
      if (!first) continue;
      const qty = first.qty || t.remainingQty || t.qty || 0;
      const delta = (first.premium - t.exitPremium) * qty;
      dedupSum += delta; dedupN += 1;
      const d = dedupByDet[first.detector] = dedupByDet[first.detector] || { n: 0, sum: 0 };
      d.n += 1; d.sum += delta;
    }
    return {
      trades: reviewed, reviews,
      exits: reviews.filter(r => r.verdict === "EXIT").length,
      holds: reviews.filter(r => r.verdict === "HOLD").length,
      byDetector,
      deltaN: withDelta.length,
      deltaSum: withDelta.reduce((a, t) => a + t.aiExitDelta, 0),
      dedupSum, dedupN, dedupByDet,
    };
  }, [filtered]);

  // day -> indices whose contracts expired that day (derived from every trade's expiry field)
  const expiryDays = useMemo(() => {
    const m = new Map();
    for (const t of trades) {
      const e = expiryOf(t);
      if (!e) continue;
      if (!m.has(e)) m.set(e, new Set());
      m.get(e).add(t.index || "?");
    }
    return m;
  }, [trades]);
  const expBadge = (day) => {
    const idx = expiryDays.get(day);
    if (!idx) return null;
    return <span title={`Contract expiry day: ${[...idx].join(", ")}`}
      style={{ marginLeft: 5, fontSize: 9, fontWeight: 700, color: "var(--warn-text)", border: "1px solid var(--warn-text)", borderRadius: 3, padding: "0 3px", whiteSpace: "nowrap" }}>
      EXP {[...idx].map(i => i === "BANKNIFTY" ? "BN" : i === "SENSEX" ? "SX" : i === "NIFTY" ? "N" : i).join("·")}</span>;
  };

  const openSlice = (label, trades, breakdowns) => setDrawerGroup({ label, trades, breakdowns });

  const applyTriggerAsFilter = (g) => {
    const t = g.ts[0];
    if (!t || !sel) return;
    setSel(prev => ({
      ...prev,
      direction: new Set([t.type || "?"]),
      fib: new Set([fibOf(t)]),
      confluence: new Set([confClass(t)]),
    }));
  };

  const total = useMemo(() => {
    const wins = filtered.filter(t => t.netPnl > 0).length;
    const pnl = filtered.reduce((a, t) => a + (t.netPnl || 0), 0);
    return { n: filtered.length, wins, pnl };
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { index: {}, direction: {}, fib: {}, confluence: {}, strategy: {}, vix: {}, tier: {}, expiryb: {}, mode: {} };
    for (const t of dateFiltered) {
      c.index[instrumentOf(t)] = (c.index[instrumentOf(t)] || 0) + 1;
      c.direction[t.type || "?"] = (c.direction[t.type || "?"] || 0) + 1;
      c.fib[fibOf(t)] = (c.fib[fibOf(t)] || 0) + 1;
      c.confluence[confClass(t)] = (c.confluence[confClass(t)] || 0) + 1;
      c.strategy[t.strategy || "?"] = (c.strategy[t.strategy || "?"] || 0) + 1;
      c.vix[vixBucket(t)] = (c.vix[vixBucket(t)] || 0) + 1;
      c.tier[tierOf(t)] = (c.tier[tierOf(t)] || 0) + 1;
      c.expiryb[expiryBucket(t)] = (c.expiryb[expiryBucket(t)] || 0) + 1;
      c.mode[modeOf(t)] = (c.mode[modeOf(t)] || 0) + 1;
    }
    return c;
  }, [dateFiltered]);

  const th = (key, label) => (
    <th onClick={() => setSortKey(key)} style={{ cursor: "pointer", textDecoration: sortKey === key ? "underline" : "none" }} title="Click to sort">
      {label}{sortKey === key ? " ↓" : ""}
    </th>
  );

  return (
    <div style={{ padding: "12px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>Trigger Analytics <span style={{ fontSize: 11, color: "var(--muted2)", fontWeight: 400 }}>closed trades from the live trade log</span></h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 11, color: "var(--dim2)" }}>min trades
            <input type="number" min="1" value={minN} onChange={e => setMinN(Math.max(1, Number(e.target.value) || 1))}
              style={{ width: 46, marginLeft: 6, background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "2px 6px" }} />
          </label>
          <button className="btn btn-sm btn-secondary" onClick={saveView} disabled={!sel} title="Save the current period + filters + sort as a named view">💾 Save view</button>
          <button className="btn btn-sm btn-secondary" onClick={load} disabled={loading}>{loading ? "Loading…" : "⟳ Refresh"}</button>
        </div>
      </div>
      {err && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 8 }}>{err}</div>}

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", fontWeight: 700 }}>Views</span>
          {ERA_VIEWS.map(e => (
            <button key={e.name} onClick={() => applyEra(e)} title={e.title}
              style={{
                background: period === "CUSTOM" && fromDate === e.from && !toDate ? "color-mix(in srgb, var(--info) 18%, transparent)" : "var(--panel3)",
                border: "1px solid var(--border2)", borderRadius: 14, color: "var(--text2)",
                fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer",
              }}>{e.name}</button>
          ))}
          {savedViews.map(v => (
            <span key={v.name} style={{ display: "inline-flex", alignItems: "center", background: "var(--panel3)", border: "1px solid var(--border2)", borderRadius: 14, overflow: "hidden" }}>
              <button onClick={() => applyView(v)} title="Apply this view"
                style={{ background: "none", border: "none", color: "var(--text2)", fontSize: 11, padding: "3px 4px 3px 10px", cursor: "pointer" }}>{v.name}</button>
              <button onClick={() => deleteView(v.name)} title="Delete this view"
                style={{ background: "none", border: "none", color: "var(--muted2)", fontSize: 11, padding: "3px 8px 3px 2px", cursor: "pointer" }}>×</button>
            </span>
          ))}
      </div>
      {sel && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", width: 78, flexShrink: 0 }}>Period</span>
            {[["TODAY", "Today"], ["7D", "7 days"], ["30D", "30 days"], ["90D", "90 days"], ["ALL", "All time"]].map(([key, label]) => (
              <Chip key={key} label={label} active={period === key} onClick={() => { setPeriod(key); setFromDate(""); setToDate(""); }} />
            ))}
            <span style={{ fontSize: 10, color: "var(--muted2)", marginLeft: 8 }}>or custom:</span>
            <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPeriod("CUSTOM"); }}
              style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "2px 6px", fontSize: 11 }} title="From date" />
            <span style={{ fontSize: 10, color: "var(--muted2)" }}>→</span>
            <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPeriod("CUSTOM"); }}
              style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "2px 6px", fontSize: 11 }} title="To date" />
            {period === "CUSTOM" && (
              <button className="btn btn-sm btn-secondary" onClick={() => { setPeriod("ALL"); setFromDate(""); setToDate(""); }}>Reset</button>
            )}
            <span style={{ fontSize: 10, color: "var(--faint2)", marginLeft: "auto" }}>{dateFiltered.length} of {trades.length} trades in period</span>
          </div>
          {sel.mode && <ChipGroup title="Mode" values={facets.mode} selected={sel.mode} onToggle={v => toggle("mode", v)} onSetAll={on => setAll("mode", on)} counts={counts.mode} />}
          <ChipGroup title="Index" values={facets.index} selected={sel.index} onToggle={v => toggle("index", v)} onSetAll={on => setAll("index", on)} counts={counts.index} />
          <ChipGroup title="Direction" values={facets.direction} selected={sel.direction} onToggle={v => toggle("direction", v)} onSetAll={on => setAll("direction", on)} counts={counts.direction} />
          <ChipGroup title="Fib level" values={facets.fib} selected={sel.fib} onToggle={v => toggle("fib", v)} onSetAll={on => setAll("fib", on)} counts={counts.fib} />
          <ChipGroup title="Confluence" values={facets.confluence} selected={sel.confluence} onToggle={v => toggle("confluence", v)} onSetAll={on => setAll("confluence", on)} counts={counts.confluence} />
          <ChipGroup title="Strategy" values={facets.strategy} selected={sel.strategy} onToggle={v => toggle("strategy", v)} onSetAll={on => setAll("strategy", on)} counts={counts.strategy} />
          {sel.vix && <ChipGroup title="India VIX" values={facets.vix} selected={sel.vix} onToggle={v => toggle("vix", v)} onSetAll={on => setAll("vix", on)} counts={counts.vix} />}
          {sel.tier && <ChipGroup title="Profit tiers" values={facets.tier} selected={sel.tier} onToggle={v => toggle("tier", v)} onSetAll={on => setAll("tier", on)} counts={counts.tier} />}
          {sel.expiryb && <ChipGroup title="Expiry" values={facets.expiryb} selected={sel.expiryb} onToggle={v => toggle("expiryb", v)} onSetAll={on => setAll("expiryb", on)} counts={counts.expiryb} />}
        </div>
      )}

      <div style={{ fontSize: 12, color: "var(--dim2)", marginBottom: 8 }}>
        Selection: {total.n} trades · {total.wins}W {total.n - total.wins}L ({total.n ? Math.round(total.wins / total.n * 100) : 0}%) ·{" "}
        {(() => { const nd = tradingDaysOf(filtered); return nd ? <>avg/day <b className={total.pnl >= 0 ? "positive" : "negative"}>{rupees(total.pnl / nd)}</b> over {nd} days ·{" "}</> : null; })()}
        <span className={total.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(total.pnl)}</span>
        {(() => {
          const { max } = dailyPeakCapital(filtered);
          return max ? <> · max capital used in a day: <b title="Highest sum of capital deployed in simultaneously-open trades (peak concurrency), across the selected days">₹{max.peak.toLocaleString("en-IN")}</b> <span style={{ color: "var(--muted2)" }}>on {max.day}</span></> : null;
        })()}
      </div>

      {/* P&L bifurcations: instrument + outcome paths stacked left, day grows right */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12, alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 460px", display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <Panel id="instr" title="P&L by instrument">
            {(() => {
              const items = byInstrument.map(r => ({ key: r.label, label: r.label,
                onClick: () => openSlice(`${r.label}`, r.trades, [{ title: "Triggers in this slice", rows: groupStats(r.trades, triggerOf) }]),
                stats: [["N", r.n], ["WR", Math.round(r.wins / r.n * 100) + "%"], ["Net", rupees(r.pnl), r.pnl >= 0 ? "positive" : "negative"], ["Avg", rupees(r.pnl / r.n), r.pnl >= 0 ? "positive" : "negative"], ["Avg/day", (() => { const nd = tradingDaysOf(r.trades); return nd ? rupees(r.pnl / nd) : "-"; })(), r.pnl >= 0 ? "positive" : "negative"]] }));
              if (byInstrument.length > 1) items.push({ key: "__total", label: "TOTAL", stats: [["N", total.n], ["WR", (total.n ? Math.round(total.wins / total.n * 100) : 0) + "%"], ["Net", rupees(total.pnl), total.pnl >= 0 ? "positive" : "negative"]] });
              return <MobileRows items={items} />;
            })()}
            <div className="tl-table-wrap">
            <table className="stocks-table" style={{ width: "100%" }}>
            <thead><tr><th>Instrument</th><th>Trades</th><th>Win rate</th><th>Net P&L</th><th>Avg / trade</th><th>Avg / day</th></tr></thead>
            <tbody>
              {byInstrument.map(r => (
                <tr key={r.label} style={{ cursor: "pointer" }} title="Click for trades + trigger breakdown"
                  onClick={() => openSlice(`${r.label}`, r.trades, [{ title: "Triggers in this slice", rows: groupStats(r.trades, triggerOf) }])}>
                  <td style={{ fontWeight: 600 }}>{r.label}</td>
                  <td>{r.n}</td>
                  <td>{Math.round(r.wins / r.n * 100)}% <span style={{ color: "var(--muted2)", fontSize: 10 }}>({r.wins}W {r.n - r.wins}L)</span></td>
                  <td className={r.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(r.pnl)}</td>
                  <td className={r.pnl >= 0 ? "positive" : "negative"}>{r.n ? rupees(r.pnl / r.n) : "-"}</td>
                  <td className={r.pnl >= 0 ? "positive" : "negative"} title="Net ÷ distinct trading days for this instrument">{(() => { const nd = tradingDaysOf(r.trades); return nd ? `${rupees(r.pnl / nd)} (${nd}d)` : "-"; })()}</td>
                </tr>
              ))}
              {byInstrument.length > 1 && (
                <tr style={{ borderTop: "2px solid var(--border2)" }}>
                  <td style={{ fontWeight: 700 }}>TOTAL</td>
                  <td>{total.n}</td>
                  <td>{total.n ? Math.round(total.wins / total.n * 100) : 0}%</td>
                  <td className={total.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(total.pnl)}</td>
                  <td className={total.pnl >= 0 ? "positive" : "negative"}>{total.n ? rupees(total.pnl / total.n) : "-"}</td>
                  <td className={total.pnl >= 0 ? "positive" : "negative"}>{(() => { const nd = tradingDaysOf(filtered); return nd ? `${rupees(total.pnl / nd)} (${nd}d)` : "-"; })()}</td>
                </tr>
              )}
              {!byInstrument.length && <tr><td colSpan={6} style={{ color: "var(--muted2)" }}>No trades in selection.</td></tr>}
            </tbody>
          </table>
            </div>
          </Panel>
          <Panel id="outcome" title="Trade outcome paths" subtitle="(booking → exit; Net = Booked leg + Runner)">
            {(() => {
              const items = tierFunnel.rows.map(r => ({ key: r.label, label: r.label,
                onClick: () => openSlice(r.label, r.trades, [{ title: "Triggers in this slice", rows: groupStats(r.trades, triggerOf) }]),
                stats: [["N", r.n], ["WR", Math.round(r.wins / r.n * 100) + "%"], ["Booked", r.bookedLeg ? rupees(r.bookedLeg) : "-", r.bookedLeg > 0 ? "positive" : ""], ["Runner", rupees(r.pnl - r.bookedLeg), (r.pnl - r.bookedLeg) >= 0 ? "positive" : "negative"], ["Net", rupees(r.pnl), r.pnl >= 0 ? "positive" : "negative"]] }));
              return <MobileRows items={items} />;
            })()}
            <div className="tl-table-wrap">
            <table className="stocks-table" style={{ width: "100%" }}>
            <thead><tr><th></th><th>Trades</th><th>WR</th><th>Booked leg</th><th>Runner*</th><th>Net P&L</th></tr></thead>
            <tbody>
              {tierFunnel.rows.map(r => (
                <tr key={r.label} style={{ cursor: "pointer" }} title="Click for trades + trigger breakdown"
                  onClick={() => openSlice(r.label, r.trades, [{ title: "Triggers in this slice", rows: groupStats(r.trades, triggerOf) }])}>
                  <td style={{ fontWeight: 600 }}>{r.label}</td>
                  <td>{r.n}</td>
                  <td>{Math.round(r.wins / r.n * 100)}%</td>
                  <td className={r.bookedLeg > 0 ? "positive" : ""}>{r.bookedLeg ? rupees(r.bookedLeg) : "-"}</td>
                  <td className={(r.pnl - r.bookedLeg) >= 0 ? "positive" : "negative"}>{rupees(r.pnl - r.bookedLeg)}</td>
                  <td className={r.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(r.pnl)}</td>
                </tr>
              ))}
              {!tierFunnel.rows.length && <tr><td colSpan={6} style={{ color: "var(--muted2)" }}>No trades in selection.</td></tr>}
              {tierFunnel.rows.length > 1 && (
                <tr style={{ borderTop: "2px solid var(--border2)" }}>
                  <td style={{ fontWeight: 700 }}>TOTAL</td>
                  <td>{total.n}</td>
                  <td>{total.n ? Math.round(total.wins / total.n * 100) : 0}%</td>
                  <td>{(() => { const b = tierFunnel.rows.reduce((a, r) => a + r.bookedLeg, 0); return b ? rupees(b) : "-"; })()}</td>
                  <td className={(total.pnl - tierFunnel.rows.reduce((a, r) => a + r.bookedLeg, 0)) >= 0 ? "positive" : "negative"}>{rupees(total.pnl - tierFunnel.rows.reduce((a, r) => a + r.bookedLeg, 0))}</td>
                  <td className={total.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(total.pnl)}</td>
                </tr>
              )}
            </tbody>
          </table>
            </div>
            <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 6, lineHeight: 1.5 }}>
            Tier 1 triggered on <b>{tierFunnel.t1}</b> trades · Tier 2 on <b>{tierFunnel.t2}</b> · both on <b>{tierFunnel.both}</b> (counts overlap).<br/>
            Every trade appears in exactly ONE row (its booking stage → its final exit), so the Net column sums to the selection total — the same figure as the P&L-by-day TOTAL.<br/>
            *Runner = Net − Booked leg (net of charges). For "No booking" rows the whole trade is the runner.
          </div>
          </Panel>
        </div>
        <div style={{ flex: "1 1 380px", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          <Panel id="day" title="P&L by day" subtitle="(newest first · 10/page)">
            <Paged items={byDay} render={pageRows => (<>
              <MobileRows items={pageRows.map(r => ({ key: r.label, label: <>{r.label}{expBadge(r.label)}</>,
                onClick: () => openSlice(r.label, r.trades, [{ title: 'By instrument', rows: groupStats(r.trades, instrumentOf) }, { title: `Triggers on ${r.label}`, rows: groupStats(r.trades, triggerOf) }]),
                stats: [["N", r.n], ["WR", Math.round(r.wins / r.n * 100) + "%"], ["Net", rupees(r.pnl), r.pnl >= 0 ? "positive" : "negative"], ["Peak cap", r.peak ? "₹" + r.peak.toLocaleString("en-IN") : "-"]] }))
                .concat(byDay.length > 1 ? [{ key: "__total", label: `TOTAL (${byDay.length} days)`, stats: [["N", total.n], ["Net", rupees(total.pnl), total.pnl >= 0 ? "positive" : "negative"]] }] : [])} />
              <div className="tl-table-wrap">
              <table className="stocks-table" style={{ width: "100%" }}>
                <thead><tr><th>Day</th><th>Trades</th><th>WR</th><th>Net P&L</th><th>Peak capital</th></tr></thead>
                <tbody>
                  {pageRows.map(r => (
                    <tr key={r.label} style={{ cursor: "pointer" }} title="Click for trades + trigger breakdown"
                      onClick={() => openSlice(r.label, r.trades, [{ title: 'By instrument', rows: groupStats(r.trades, instrumentOf) }, { title: `Triggers on ${r.label}`, rows: groupStats(r.trades, triggerOf) }])}>
                      <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{r.label}{expBadge(r.label)}</td>
                      <td>{r.n}</td>
                      <td>{Math.round(r.wins / r.n * 100)}%</td>
                      <td className={r.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(r.pnl)}</td>
                      <td>{r.peak ? `₹${r.peak.toLocaleString("en-IN")}` : "-"}</td>
                    </tr>
                  ))}
                  {!byDay.length && <tr><td colSpan={5} style={{ color: "var(--muted2)" }}>No trades in selection.</td></tr>}
                  {byDay.length > 1 && (
                    <tr style={{ borderTop: "2px solid var(--border2)" }}>
                      <td style={{ fontWeight: 700 }}>TOTAL ({byDay.length} days)</td>
                      <td>{total.n}</td>
                      <td>{total.n ? Math.round(total.wins / total.n * 100) : 0}%</td>
                      <td className={total.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(total.pnl)}</td>
                      <td title="Highest single-day peak (sum of daily peaks isn't meaningful)">max ₹{Math.max(...byDay.map(r => r.peak || 0)).toLocaleString("en-IN")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </>)} />
          </Panel>
          <Panel id="vix" title="P&L by VIX at entry" subtitle="(vixEntry stamped live at signal time — regime check: wins shrink & losses swell above ~13)">
            {(() => {
              const items = byVix.map(r => ({ key: r.label, label: `VIX ${r.label}`,
                onClick: () => openSlice(`VIX ${r.label}`, r.trades, [{ title: "Triggers in this slice", rows: groupStats(r.trades, triggerOf) }]),
                stats: [["N", r.n], ["WR", Math.round(r.wins / r.n * 100) + "%"], ["Net", rupees(r.pnl), r.pnl >= 0 ? "positive" : "negative"], ["Avg", rupees(r.pnl / r.n), r.pnl >= 0 ? "positive" : "negative"]] }));
              return <MobileRows items={items} />;
            })()}
            <div className="tl-table-wrap">
            <table className="stocks-table" style={{ width: "100%" }}>
              <thead><tr><th>VIX at entry</th><th>Trades</th><th>Win rate</th><th>Net P&L</th><th>Avg / trade</th><th>Avg win</th><th>Avg loss</th></tr></thead>
              <tbody>
                {byVix.map(r => {
                  const w = r.trades.filter(t => t.netPnl > 0), l = r.trades.filter(t => t.netPnl <= 0);
                  const aw = w.length ? w.reduce((a, t) => a + t.netPnl, 0) / w.length : null;
                  const al = l.length ? l.reduce((a, t) => a + t.netPnl, 0) / l.length : null;
                  return (
                    <tr key={r.label} style={{ cursor: "pointer" }} title="Click for trades + trigger breakdown"
                      onClick={() => openSlice(`VIX ${r.label}`, r.trades, [{ title: "Triggers in this slice", rows: groupStats(r.trades, triggerOf) }])}>
                      <td style={{ fontWeight: 600 }}>{r.label}</td>
                      <td>{r.n}</td>
                      <td>{Math.round(r.wins / r.n * 100)}% <span style={{ color: "var(--muted2)", fontSize: 10 }}>({r.wins}W {r.n - r.wins}L)</span></td>
                      <td className={r.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(r.pnl)}</td>
                      <td className={r.pnl >= 0 ? "positive" : "negative"}>{rupees(r.pnl / r.n)}</td>
                      <td className="positive">{aw != null ? rupees(aw) : "-"}</td>
                      <td className="negative">{al != null ? rupees(al) : "-"}</td>
                    </tr>
                  );
                })}
                {!byVix.length && <tr><td colSpan={7} style={{ color: "var(--muted2)" }}>No trades in selection.</td></tr>}
              </tbody>
            </table>
            </div>
            <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 6, lineHeight: 1.5 }}>
              Verified 01 Oct 2026 (era, ex-Friday/ex-25Sep): below VIX 13 every band earns; above 13 the win rate HOLDS (~60%) but avg win halves and avg loss doubles → avg −₹5.3k/trade.
              Candidate rule parked pending more sample: half lots when VIX &gt; 13. The India VIX filter chips above slice every other panel by these same buckets.
            </div>
          </Panel>
          <Panel id="fibdepth" title="Fib depth paper filters" subtitle="(what-if only — no trade is ever skipped)">
            <FibDepthPaperFilters />
          </Panel>
          <Panel id="ai" title="AI exit suggestions" subtitle="(advisory — forward measurement)">
            <AiUsageStrip />
            {aiStats.reviews.length ? (
              <>
                <div style={{ fontSize: 12, color: "var(--dim2)", marginBottom: 8, lineHeight: 1.6 }}>
                  <b>{aiStats.reviews.length}</b> reviews on <b
                    style={{ cursor: "pointer", textDecoration: "underline dotted" }}
                    onClick={() => openSlice("AI-reviewed trades", aiStats.trades)}
                  >{aiStats.trades.length}</b> trades ·{" "}
                  <span className="negative">{aiStats.exits} EXIT</span> · <span className="positive">{aiStats.holds} HOLD</span>
                </div>
                <div className="tl-table-wrap">
                <table className="stocks-table" style={{ width: "100%" }}>
                  <thead><tr><th>Detector</th><th>Reviews</th><th>EXIT verdicts</th></tr></thead>
                  <tbody>
                    {Object.entries(aiStats.byDetector).map(([d, s]) => (
                      <tr key={d}><td style={{ fontWeight: 600 }}>{d}</td><td>{s.n}</td><td>{s.exits}</td></tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <div style={{ fontSize: 11.5, marginTop: 8 }}>
                  Following the <b>first</b> AI exit per trade (deduped, {aiStats.dedupN} closed trades) would have{" "}
                  <b className={aiStats.dedupSum >= 0 ? "positive" : "negative"}>
                    {aiStats.dedupSum >= 0 ? "saved " : "cost "}{rupees(Math.abs(aiStats.dedupSum))}
                  </b>
                  {Object.keys(aiStats.dedupByDet || {}).length > 0 && (
                    <span style={{ color: "var(--muted2)" }}>
                      {" — "}{Object.entries(aiStats.dedupByDet).map(([d, v]) => `${d} ${v.sum >= 0 ? "+" : "−"}₹${Math.abs(Math.round(v.sum)).toLocaleString("en-IN")}/${v.n}`).join(" · ")}
                    </span>
                  )}
                  <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 2 }}>
                    Caution: verdicts were 100% EXIT until the 08-Oct prompt rebalance — credit so far belongs to the detectors, not AI discrimination. Checkpoint: re-audit at 50 deduped reviews.
                  </div>
                </div>
                <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 6, lineHeight: 1.5 }}>
                  The AI never exits trades — this scoreboard is the evidence that decides if it ever should.
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: "var(--muted2)" }}>No AI reviews in the current selection yet. Reviews appear when a detector flags an open trade as behaving oddly.</div>
            )}
          </Panel>
        </div>
      </div>

      {/* Day × instrument matrix — rows are days (newest first), columns are
          instruments; row totals = day P&L, bottom row = instrument totals. */}
      <div style={{ marginBottom: 12 }}>
        <Panel id="daymatrix" title="P&L by day × instrument" subtitle="(cells clickable — day + instrument slice)">
          {(() => {
            const instruments = byInstrument.map(r => r.label);
            const cell = new Map(); // `${day}|${instr}` -> {n, pnl, trades}
            for (const t of filtered) {
              const k = `${dayOf(t)}|${instrumentOf(t)}`;
              if (!cell.has(k)) cell.set(k, { n: 0, pnl: 0, trades: [] });
              const c = cell.get(k);
              c.n++; c.pnl += t.netPnl || 0; c.trades.push(t);
            }
            const days = byDay.map(r => r.label); // already newest-first
            if (!days.length || !instruments.length) return <div style={{ fontSize: 12, color: "var(--muted2)" }}>No trades in selection.</div>;
            return (
              <Paged items={days} render={pageDays => (
              <div className="tl-table-wrap">
                <table className="stocks-table" style={{ width: "100%" }}>
                  <thead><tr>
                    <th>Day</th>
                    {instruments.map(i => <th key={i}>{i}</th>)}
                    <th>Day total</th>
                  </tr></thead>
                  <tbody>
                    {pageDays.map(d => {
                      const dayRow = byDay.find(r => r.label === d);
                      return (
                        <tr key={d}>
                          <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{d}{expBadge(d)}</td>
                          {instruments.map(i => {
                            const c = cell.get(`${d}|${i}`);
                            if (!c) return <td key={i} style={{ color: "var(--faint2)" }}>·</td>;
                            return (
                              <td key={i} className={c.pnl >= 0 ? "positive" : "negative"} style={{ cursor: "pointer" }}
                                title={`${c.n} trade${c.n === 1 ? "" : "s"} — click to open`}
                                onClick={() => openSlice(`${d} · ${i}`, c.trades, [{ title: "Triggers in this slice", rows: groupStats(c.trades, triggerOf) }])}>
                                {rupees(c.pnl)} <span style={{ color: "var(--muted2)", fontSize: 9.5 }}>({c.n})</span>
                              </td>
                            );
                          })}
                          <td className={dayRow && dayRow.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{dayRow ? rupees(dayRow.pnl) : "-"}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: "2px solid var(--border2)" }}>
                      <td style={{ fontWeight: 700 }}>TOTAL</td>
                      {instruments.map(i => {
                        const r = byInstrument.find(x => x.label === i);
                        return <td key={i} className={r && r.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{r ? rupees(r.pnl) : "-"}</td>;
                      })}
                      <td className={total.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(total.pnl)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )} />
            );
          })()}
        </Panel>
      </div>

      {/* Brokerage & taxes — same formula the monitor charges at close */}
      <div style={{ marginBottom: 12 }}>
        <Panel id="charges" title="Brokerage & taxes" subtitle="(per the exchange/broker formula applied at close)">
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            {[["day", "By day"], ["instrument", "By instrument"], ["trade", "By trade"]].map(([k, l]) => (
              <Chip key={k} label={l} active={chargesView === k} onClick={() => setChargesView(k)} />
            ))}
          </div>
          {(() => {
            const grand = sumCharges(filtered);
            const pctOfGross = (tot, ts2) => {
              const g = ts2.reduce((a, t) => a + Math.abs(t.grossPnl != null ? t.grossPnl : (t.netPnl || 0)), 0);
              return g ? `${(tot / g * 100).toFixed(1)}%` : "-";
            };
            const header = (first) => (
              <thead><tr><th>{first}</th><th>Trades</th>{CHARGE_KEYS.map(([k, l]) => <th key={k}>{l}</th>)}<th>Total</th><th>vs |gross|</th></tr></thead>
            );
            const rowCells = (c, ts2) => (<>
              <td>{c.n}</td>
              {CHARGE_KEYS.map(([k]) => <td key={k}>{c[k] ? `₹${Math.round(c[k]).toLocaleString("en-IN")}` : "-"}</td>)}
              <td style={{ fontWeight: 700 }}>₹{Math.round(c.total).toLocaleString("en-IN")}</td>
              <td style={{ color: "var(--muted2)" }}>{pctOfGross(c.total, ts2)}</td>
            </>);
            if (chargesView === "trade") {
              const rows = [...filtered].sort((a, b) => String(b.time).localeCompare(String(a.time))).slice(0, 400);
              return (
                <Paged items={rows} render={pageRows2 => (
                <div className="tl-table-wrap">
                  <table className="stocks-table" style={{ width: "100%" }}>
                    <thead><tr><th>Time</th><th>Instrument</th><th>Contract</th><th>Turnover</th>{CHARGE_KEYS.map(([k, l]) => <th key={k}>{l}</th>)}<th>Total</th></tr></thead>
                    <tbody>
                      {pageRows2.map((t, i) => {
                        const b = chargesBreakdown(t);
                        return (
                          <tr key={t.id || i} style={{ cursor: "pointer" }} onClick={() => setDrawerTrade(t)} title="Click for full trade details">
                            <td style={{ whiteSpace: "nowrap" }}>{new Date(t.time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                            <td>{instrumentOf(t)}</td>
                            <td style={{ fontSize: 10.5 }}>{t.tradingsymbol || "-"}</td>
                            <td>{b ? `₹${Math.round(b.turnover).toLocaleString("en-IN")}` : "-"}</td>
                            {CHARGE_KEYS.map(([k]) => <td key={k}>{b ? `₹${b[k].toFixed(1)}` : "-"}</td>)}
                            <td style={{ fontWeight: 700 }}>₹{b ? b.total.toFixed(1) : (t.charges ?? "-")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )} />
              );
            }
            const keyFn = chargesView === "day" ? dayOf : instrumentOf;
            const groups2 = new Map();
            for (const t of filtered) {
              const k = keyFn(t);
              if (!groups2.has(k)) groups2.set(k, []);
              groups2.get(k).push(t);
            }
            const rows = [...groups2.entries()].sort((a, b) => chargesView === "day" ? b[0].localeCompare(a[0]) : sumCharges(b[1]).total - sumCharges(a[1]).total);
            return (
              <Paged items={rows} render={pageRows3 => (
              <div className="tl-table-wrap">
                <table className="stocks-table" style={{ width: "100%" }}>
                  {header(chargesView === "day" ? "Day" : "Instrument")}
                  <tbody>
                    {pageRows3.map(([label, ts2]) => (
                      <tr key={label}>
                        <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{label}{chargesView === "day" ? expBadge(label) : null}</td>
                        {rowCells(sumCharges(ts2), ts2)}
                      </tr>
                    ))}
                    <tr style={{ borderTop: "2px solid var(--border2)" }}>
                      <td style={{ fontWeight: 700 }}>TOTAL</td>
                      {rowCells(grand, filtered)}
                    </tr>
                  </tbody>
                </table>
              </div>
              )} />
            );
          })()}
          <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 6, lineHeight: 1.5 }}>
            Breakdown recomputed from entry/exit premiums with the monitor's close-time formula (brokerage min ₹20/leg ×2 · STT 0.0625% sell · exch 0.053% turnover · GST 18% · SEBI 0.0001% · stamp 0.003% buy) — verified to reconcile with every stored trade total. "vs |gross|" = charges share of absolute gross P&L. Trade view shows the latest 400 of the selection; respects all active filters and era views.
          </div>
        </Panel>
      </div>
      <Paged items={groups} render={pageGroups => (<>
      <MobileRows items={pageGroups.map(g => ({ key: g.label, label: g.label,
          onClick: () => openSlice(g.label, g.ts, [
            { title: "By instrument", rows: groupStats(g.ts, instrumentOf) },
            { title: "By day", rows: groupStats(g.ts, dayOf, true) },
          ]),
          stats: [["N", g.n], ["WR", Math.round(g.wr * 100) + "%"], ["Net", rupees(g.pnl), g.pnl >= 0 ? "positive" : "negative"], ["Avg", rupees(g.avg), g.avg >= 0 ? "positive" : "negative"]] }))} />
      <div className="stocks-table-wrapper tl-table-wrap">
        <table className="stocks-table" style={{ width: "100%" }}>
          <thead><tr>
            <th>Trigger</th>{th("n", "Trades")}{th("wr", "Win rate")}{th("pnl", "Net P&L")}{th("avg", "Avg / trade")}
          </tr></thead>
          <tbody>
            {pageGroups.map(g => (
              <tr key={g.label} style={{ cursor: "pointer" }} title="Click to see the trades"
                onClick={() => openSlice(g.label, g.ts, [
                  { title: "By instrument", rows: groupStats(g.ts, instrumentOf) },
                  { title: "By day", rows: groupStats(g.ts, dayOf, true) },
                ])}>
                <td style={{ fontWeight: 600 }}>
                  <button onClick={(e) => { e.stopPropagation(); applyTriggerAsFilter(g); }}
                    title="Apply this trigger as page filters (direction + fib + confluence)"
                    style={{ background: "none", border: "1px solid var(--border2)", borderRadius: 4, color: "#3b82f6", fontSize: 10, padding: "1px 6px", marginRight: 8, cursor: "pointer" }}>⧩ filter</button>
                  {g.label}
                </td>
                <td>{g.n}</td>
                <td>{Math.round(g.wr * 100)}% <span style={{ color: "var(--muted2)", fontSize: 10 }}>({g.wins}W {g.n - g.wins}L)</span></td>
                <td className={g.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rupees(g.pnl)}</td>
                <td className={g.avg >= 0 ? "positive" : "negative"}>{rupees(g.avg)}</td>
              </tr>
            ))}
            {!groups.length && <tr><td colSpan={5} style={{ color: "var(--muted2)" }}>No trigger groups match the current selection (or below the min-trades cutoff).</td></tr>}
          </tbody>
        </table>
      </div>
      </>)} />

      {drawerGroup && (
        <TriggerDrawer group={drawerGroup.label} trades={drawerGroup.trades} breakdowns={drawerGroup.breakdowns} tradeOpen={!!drawerTrade}
          onClose={() => setDrawerGroup(null)} onPickTrade={t => setDrawerTrade(t)} />
      )}
      {drawerTrade && <TradeDrawer trade={drawerTrade} onClose={() => setDrawerTrade(null)} />}
    </div>
  );
}
